import { Link } from 'react-router-dom';
import { Section } from '../components/Section';
import { readConsent, writeConsent } from '../lib/consent';
import { usePageMeta } from '../lib/usePageMeta';
import { useSettings } from '../lib/useSettings';
import { overflowBookingUrl } from '../lib/overflow';
import { SEO } from '../seo/pages';

const h = 'mt-8 text-2xl';
const p = 'mt-3 text-ink-200';
const link = 'text-pink-300 underline hover:text-pink-200';

export function TermsPage() {
  usePageMeta(SEO.terms.title, SEO.terms.description);
  const { data: s } = useSettings();
  return (
    <Section narrow eyebrow="The fine print" title="Terms">
      <h2 className={h}>Rooms booked on this site</h2>
      <p className={p}>
        The celebration moved venue on 15 September 2026 and the hotel room block went with it.{' '}
        <strong>
          Every room reservation made on this site is being refunded to the card that paid for it.
        </strong>{' '}
        There is nothing you need to do, and this site no longer sells rooms or anything else.
        Accommodation is booked with the hotel of your choice through{' '}
        <a
          href={overflowBookingUrl(s?.eventStartDate)}
          target="_blank"
          rel="noopener noreferrer"
          className={link}
        >
          SecretsOverflow
        </a>
        , on their terms and not ours.
      </p>
      <h2 className={h}>RSVPs and donations</h2>
      <p className={p}>
        An RSVP puts the named people on the list at the door and nothing else. It is free, and so
        is entry to the celebration between 7:00 and 10:00 PM. Any donation you add is a gift to
        Shawna, in the amount you choose, and buys nothing; gifts are{' '}
        <strong>final and non-refundable</strong>.
      </p>
      <h2 className={h}>Payment and who is charging you</h2>
      <p className={p}>
        Payments are processed by MobiusPay. Your card details go straight into the payment
        processor&apos;s secure form and are never seen or stored by this site; we keep only the
        card brand and last four digits with your RSVP.
      </p>
      <p className={p}>
        The charge appears on your card statement as <strong>Icandy Enterprises</strong>, which
        collected room payments and collects RSVP donations on behalf of Brandon&apos;s family. A
        refunded room reverses against that same descriptor and can take a few days to appear,
        depending on your bank.
        {s?.contactEmail ? ` Questions about a charge: ${s.contactEmail}.` : ''}
      </p>
      <h2 className={h}>Governing law</h2>
      <p className={p}>
        These terms are governed by the laws of the State of Florida, where the celebration is held
        and the organisers are based.
      </p>
      <h2 className={h}>Questions</h2>
      <p className={p}>
        {s?.contactEmail ? `Email ${s.contactEmail}, or use the ` : 'Use the '}
        <Link to="/contact" className={link}>
          contact page
        </Link>
        .
      </p>
    </Section>
  );
}

export function PrivacyPage() {
  usePageMeta(SEO.privacy.title, SEO.privacy.description);
  const { data: s } = useSettings();
  const resetConsent = () => {
    writeConsent(null);
    window.location.reload();
  };
  return (
    <Section narrow eyebrow="Your information" title="Privacy">
      <h2 className={h}>Who runs this site</h2>
      <p className={p}>
        BrandonStrong.com is operated by Icandy Enterprises, Lakeland, Florida, on behalf of
        Brandon&apos;s family. It exists for one event and collects only what is needed to put your
        party on the door list.
      </p>
      <h2 className={h}>What we collect and why</h2>
      <p className={p}>
        When you RSVP we keep your contact details and the first and last name of everyone in your
        party, which go on the printed list at the door, plus the card brand and last four digits
        and the IP address and browser of the request (to stop fraud and bots) if you add a
        donation. Room reservations made here before the venue moved also hold a name, email, phone,
        billing address, card brand and last four digits; those records stay only until the refunds
        are settled. We do not sell any of it or share it with anyone other than the payment
        processor.
      </p>
      <h2 className={h}>Card data</h2>
      <p className={p}>
        Full card numbers are handled only by MobiusPay&apos;s hosted payment fields on the RSVP
        donation, loaded from secure.mobiusgateway.com. This site and its servers never see them.
      </p>
      <h2 className={h}>Cookies</h2>
      <p className={p}>
        The public site sets no cookies of its own. The admin area sets one strictly necessary
        sign-in cookie for the family members who manage the site. Google Analytics cookies are set
        only if you accept them in the notice at the bottom of the page.{' '}
        {readConsent() ? (
          <>
            Your current choice is <strong>{readConsent()}</strong>.{' '}
            <button type="button" className={link} onClick={resetConsent}>
              Change it
            </button>
            .
          </>
        ) : null}
      </p>
      <h2 className={h}>Services we rely on</h2>
      <ul className={`${p} list-disc pl-5`}>
        <li>MobiusPay (payment processing; hosted card fields on the RSVP donation).</li>
        <li>
          Google reCAPTCHA on the RSVP form, to block bots; subject to Google&apos;s privacy policy
          and terms.
        </li>
        <li>Google Fonts, which serves the typefaces and sees your IP address when they load.</li>
        <li>Google Analytics, only with your consent, to see which pages help people.</li>
        <li>
          Sentry error monitoring, which records technical details when something breaks; form
          contents are masked.
        </li>
        <li>
          Amazon Web Services, which hosts the site, and Google Workspace, which relays the
          confirmation emails.
        </li>
      </ul>
      <h2 className={h}>How long we keep it</h2>
      <p className={p}>
        RSVP records are kept until the celebration is over, and the remaining reservation records
        until every refund and card dispute is closed; then they are deleted. Analytics data is
        aggregated and not tied to your name.
      </p>
      <h2 className={h}>Your choices</h2>
      <p className={p}>
        Email us to see, correct or delete what we hold about you. Deleting an RSVP before the
        celebration takes your party off the list at the door.
      </p>
      <h2 className={h}>Contact</h2>
      <p className={p}>
        {s?.contactEmail
          ? `Email ${s.contactEmail} with any questions about your information, or use the `
          : 'Use the '}
        <Link to="/contact" className={link}>
          contact page
        </Link>
        .
      </p>
    </Section>
  );
}
